import React, { useEffect, useState } from "react";
import Button from "../../../Button";
import Input from "../../../Input";
import TextView from "../../../TextView";
import {
  convertEmbedCodeToReact,
  extractSoundCloudTitle,
  fetchSpotifyDetails,
} from "./helpers";
import { colors } from "../../../../utils";
import { getRandomId } from "../../../../utils/helpers";
import { saveRecentMedia } from "../../../../services/api";
import { useSelector } from "react-redux";
import { getWheelId } from "../../../../redux/selectors/segmentBuilderSelectors";

const EmbedCode = ({ onConfirm, media }) => {
  const [embedCode, setEmbedCode] = useState(media?.originalSrc || "");
  const [convertedCode, setConvertedCode] = useState("");
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const wheelId = useSelector(getWheelId);

  const handleValidations = (url) => {
    if (!url) {
      return { isValid: false, message: "URL cannot be empty." };
    }

    const patterns = {
      spotify:
        /^https?:\/\/(open\.)?spotify\.com\/(embed\/)?(track|playlist|album|show|episode|artist)\/[a-zA-Z0-9]+/,
      soundcloud:
        /^https?:\/\/(w\.)?soundcloud\.com\/(([\w-]+\/[\w-]+)|(player\/\?url=https%3A\/\/api\.soundcloud\.com\/(tracks|playlists|albums)\/[^&]+))/,

      // lastfm: /^https?:\/\/(www\.)?last\.fm\/music\/[a-zA-Z0-9_+-]+\/[a-zA-Z0-9_+-]+/,
      // listenNotes: /^https?:\/\/(www\.)?listennotes\.com\/(e)\/[a-zA-Z0-9_-]+/,
    };

    for (const [platform, pattern] of Object.entries(patterns)) {
      if (pattern.test(url)) {
        return { isValid: true, platform, message: `Valid ${platform} URL.` };
      }
    }

    return {
      isValid: false,
      message: "URL is not valid for supported platforms.",
    };
  };

  const handlePaste = (event) => {
    event.preventDefault();

    const pastedData = event.clipboardData.getData("text");
    setEmbedCode(pastedData);
  };

  const handleConfirm = () => {
    if (!embedCode.includes("iframe")) {
      return setError(
        <>
          {" "}
          <b>Invalid Code:</b> Please only paste code directly from the source
          application’s ‘Embed Track’ or ‘Embed Code’ feature. Currently only
          accepting code from <b>Spotify</b> and <b>SoundCloud</b>.
        </>
      );
    }
    const code = convertEmbedCodeToReact(embedCode, setUrl);
    setConvertedCode(code);
  };

  const handleClear = () => {
    setEmbedCode("");
    setConvertedCode("");
    setError("");
  };

  useEffect(() => {
    const fetchDetails = async () => {
      const { isValid, platform } = handleValidations(url);

      if (!isValid)
        return setError(
          <>
            <b>Invalid Code:</b> Please only paste code directly from the source
            application’s ‘Embed Track’ or ‘Embed Code’ feature. Currently only
            accepting code from <b>Spotify</b> and <b>SoundCloud</b>.
          </>
        );

      switch (platform) {
        case "spotify":
          try {
            const id = getRandomId();
            setLoading(true);
            onConfirm({ id, loading: true });
            const { title, image } = await fetchSpotifyDetails(url);
            setLoading(false);
            onConfirm({
              id,
              embedCode: convertedCode,
              embed: true,
              title,
              image,
              originalSrc: embedCode.toString(),
              loading: false,
            });
            await saveRecentMedia(wheelId, {
              type: "3",
              url,
              metadata: {
                thumbnail: image,
                title,
                type: "spotify",
                embedCode: convertedCode,
              },
            });
            handleClear();
          } catch (err) {
            setLoading(false);
            setError("Failed to fetch track details.");
            onConfirm({ id: getRandomId(), loading: false });
          }
          break;

        case "soundcloud":
          const title = extractSoundCloudTitle(embedCode);
          onConfirm({
            id: getRandomId(),
            embedCode: convertedCode,
            embed: true,
            title,
            originalSrc: embedCode.toString(),
          });
          await saveRecentMedia(wheelId, {
            type: "3",
            url,
            metadata: {
              title,
              type: "soundcloud",
              embedCode: convertedCode,
            },
          });
          break;

        default:
      }
    };

    if (url) fetchDetails();
  }, [url]);

  return (
    <div style={{ marginTop: 20 }}>
      <TextView>
        Paste CSS code from Spotify or SoundCloud here to display an audio
        playbar for your track. Simply copy the embed code and paste it here to
        generate the playbar!
      </TextView>
      <Input
        id="embedCode"
        type="textarea"
        variant={"secondary"}
        value={embedCode}
        onPaste={handlePaste}
        containerStyle={{ marginBottom: 0 }}
      />
      <div
        style={{
          padding: "5px 10px",
          background: error ? "#FFEFEF" : colors.background,
          borderRadius: 5,
          marginTop: 10,
        }}
      >
        {error ? (
          <TextView>{error}</TextView>
        ) : (
          <TextView>
            <b>Note:</b> The code pasted into this text area cannot be edited
            directly. If you want to replace the code, press the <b>'Clear' </b>{" "}
            button to reset the field and start again.
          </TextView>
        )}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "100px 100px",
          gridGap: 10,
          justifyContent: "end",
          marginTop: 20,
        }}
      >
        <Button
          variant={"secondary"}
          style={{ height: 36 }}
          onClick={handleClear}
        >
          Clear
        </Button>
        <Button
          style={{ height: 36 }}
          onClick={handleConfirm}
          disabled={loading}
        >
          Confirm
        </Button>
      </div>
    </div>
  );
};

export default EmbedCode;
